import type { ToastT } from 'sonner'

/*
  Overrides for how the global fetcher toasts a failed response. Some backend
  "errors" are expected states (a risk account that was never ingested, the OTP
  step during login) and shouldn't surface as a red error toast. Add a case by
  appending a rule — the first match wins.
*/

type ErrorBody = {
  otp_required?: boolean
  error?: { details?: Array<{ code?: string }> }
} | null

export type ErrorToastContext = {
  url: string
  status: number
  method?: string
  isQuery?: boolean
  errorData: ErrorBody
}

// 'suppress' = no toast; object = override type/message; undefined = default red toast.
export type ErrorToastDecision = 'suppress' | { type?: ToastT['type']; message?: string } | undefined

const hasDetailCode = (errorData: ErrorBody, code: string) =>
  errorData?.error?.details?.some((detail) => detail?.code === code) ?? false

export const RISK_MATCHING_PATH = '/admin/api/risk_engine/matching/'
export const OTP_REQUIRED_MESSAGE = '2FA (otp) is required'

const RULES: Array<{ match: (ctx: ErrorToastContext) => boolean; decision: ErrorToastDecision }> = [
  // Risk-engine matching 422 with a `not_found` code: the account was never
  // ingested. The UI shows an empty "No similar accounts" state, so a toast would
  // just be noise. Other 422s on this endpoint are real errors and still toast.
  {
    match: ({ url, status, errorData }) =>
      status === 422 && url.includes(RISK_MATCHING_PATH) && hasDetailCode(errorData, 'not_found'),
    decision: 'suppress',
  },
  // A 403 on a background query means the UI loaded something the session's keys don't grant — a missing
  // gate, not something the user did. Stay quiet (the section renders without data). Anything the user
  // triggered still toasts: writes, and direct GETs such as CSV exports or file downloads.
  {
    match: ({ status, method = 'GET', isQuery = false }) => status === 403 && method === 'GET' && isQuery,
    decision: 'suppress',
  },
  // Login OTP step: a prompt for the second factor, not a failure.
  {
    match: ({ errorData }) => errorData?.otp_required === true || hasDetailCode(errorData, 'otp_required'),
    decision: { type: 'info', message: OTP_REQUIRED_MESSAGE },
  },
]

export const classifyErrorToast = (ctx: ErrorToastContext): ErrorToastDecision =>
  RULES.find((rule) => rule.match(ctx))?.decision
